import { AnalyticsBrowser, Analytics } from '@segment/analytics-next';
import { v4 as uuidv4 } from 'uuid';
import * as sessionReplay from '@amplitude/session-replay-browser';
import { debugLogClientTracking, type ClientTrackingDebugMeta } from './trackingDebug';

// Cookie utility functions for analytics_session_id
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

const setCookie = (name: string, value: string): void => {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
};

const getStoredSessionId = (): number => {
  const sessionId = getCookie('analytics_session_id');
  return sessionId ? parseInt(sessionId, 10) : 0;
};

type TrackEvent =
  | 'Screen Displayed'
  | 'Button Pressed'
  | 'Error Occurred'
  | 'Game Session Start'
  | 'Game Session End';

type EventSubCategory =
  | 'sign-in'
  | 'select-room'
  | 'game-prejoin'
  | 'character-creation-entry'
  | 'character-creation'
  | 'landing-page'
  | 'game-start'
  | 'cinematic-playback'
  | 'quest-complete'
  | 'game-over'
  | 'error-modal'
  | 'audio-blocked-modal'
  | 'reconnecting'
  | 'quest-selection'
  | 'manage-subscription'
  | 'log-out'
  | 'discord'
  | 'anonymous-start'
  | 'character-share-overlay'
  | 'character-share-page'
  | 'polaroid-overlay'
  | 'image-share-page';

interface BaseEventProperties {
  timestamp: string;
  sessionId?: string;
  roomName?: string;
  gameInstanceId?: string;
  userId?: string;
}

interface GameEventProperties extends BaseEventProperties {
  source: 'client' | 'server';
  eventCategory: string;
  eventSubCategory?: EventSubCategory;
  message: string;
  status: 'handled' | 'unhandled';
  displayChoices?: string[];
  choiceValue?: string;
  screenDisplayedId?: string;
  text?: string;
  productId?: string;
  // Experiment / A/B testing context
  landingPageVariant?: string;
  experimentKey?: string;
  // Referral tracking
  referralSource?: string;
  characterId?: string;
  imageId?: string;
}

class SegmentClient {
  private analytics: Analytics | null = null;
  private sessionId: string | null = null;
  private userId: string | null = null;
  private roomName: string | null = null;
  private gameInstanceId: string | null = null;
  private sessionReplayInitialized = false;
  private initializeAttempted = false;
  private initializeFailed = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.sessionId = this.generateSessionId();
    }
  }

  private generateSessionId(): string {
    return `session_${uuidv4()}`;
  }

  async initialize(): Promise<Analytics | null> {
    if (this.analytics) {
      return this.analytics;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    // If Segment load previously failed (common on restricted networks/ad-blockers),
    // don't keep retrying and don't let analytics availability block app flows.
    if (this.initializeAttempted && this.initializeFailed) {
      return null;
    }

    const writeKey = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
    if (!writeKey) return null;

    this.initializeAttempted = true;
    try {
      const [analytics] = await AnalyticsBrowser.load({ writeKey });
      this.analytics = analytics;
      this.initializeFailed = false;

      // Initialize Amplitude Session Replay if API key is available
      await this.initializeSessionReplay(analytics);

      // Set up global error handler
      window.addEventListener('error', (event) => {
        // Best-effort: never throw from handler
        void this.track('Error Occurred', {
          source: 'client',
          message: event.error?.message || 'Unknown error',
          status: 'unhandled',
          eventCategory: '',
        });
      });
    } catch (error) {
      this.analytics = null;
      this.initializeFailed = true;
      console.warn('Segment failed to initialize (continuing without analytics)', error);
      return null;
    }

    return this.analytics;
  }

  private async initializeSessionReplay(analytics: Analytics): Promise<void> {
    if (this.sessionReplayInitialized) {
      return;
    }

    const amplitudeApiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
    if (!amplitudeApiKey) {
      console.warn(
        'NEXT_PUBLIC_AMPLITUDE_API_KEY not found. Session Replay will not be initialized.',
      );
      return;
    }

    try {
      const user = await analytics.user();
      const storedSessionId = getStoredSessionId();

      // Initialize session replay with stored session ID and anonymous device ID
      const deviceId = user.anonymousId() || undefined;
      await sessionReplay.init(amplitudeApiKey, {
        sessionId: storedSessionId,
        deviceId: deviceId,
      }).promise;

      this.sessionReplayInitialized = true;
      console.log('Amplitude Session Replay initialized successfully');

      // Add middleware to check if the session id has changed
      analytics.addSourceMiddleware(({ payload, next }) => {
        const storedSessionId = getStoredSessionId();
        const amplitudeIntegration = payload.obj.integrations?.['Actions Amplitude'];
        const nextSessionId =
          typeof amplitudeIntegration === 'object' &&
          amplitudeIntegration &&
          'session_id' in amplitudeIntegration
            ? (amplitudeIntegration.session_id as number)
            : 0;

        if (storedSessionId < nextSessionId) {
          setCookie('analytics_session_id', nextSessionId.toString());
          sessionReplay.setSessionId(nextSessionId);
        }
        next(payload);
      });

      // Add middleware to always add session replay properties to track calls
      analytics.addSourceMiddleware(({ payload, next }) => {
        const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
        if (payload.type() === 'track' && sessionReplayProperties) {
          payload.obj.properties = {
            ...payload.obj.properties,
            ...sessionReplayProperties,
          };
        }
        next(payload);
      });
    } catch (error) {
      console.error('Failed to initialize Amplitude Session Replay:', error);
    }
  }

  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    try {
      const analytics = await this.initialize();
      const anonymousId = analytics?.user()?.anonymousId?.();

      this.userId = userId;

      debugLogClientTracking({
        anonymousId: anonymousId || undefined,
        emitter: 'client',
        kind: 'identify',
        pipeline: 'segment-client',
        traits,
        userId,
      });

      if (!analytics) return;

      analytics.identify(userId, {
        ...traits,
        sessionId: this.sessionId,
        anonymousId,
        platform: 'web',
      });
    } catch (error) {
      console.warn('Segment identify failed (ignored)', error);
    }
  }

  async track(
    event: TrackEvent,
    properties?: Partial<GameEventProperties>,
    debugMeta?: ClientTrackingDebugMeta,
  ): Promise<void> {
    try {
      const baseProperties: BaseEventProperties = {
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId || undefined,
        roomName: this.roomName || undefined,
        gameInstanceId: this.gameInstanceId || undefined,
        userId: this.userId || undefined,
      };

      const mergedProperties = {
        ...baseProperties,
        ...properties,
      };

      debugLogClientTracking({
        emitter: 'client',
        eventName: event,
        kind: 'track',
        pipeline: debugMeta?.pipeline || 'segment-client',
        properties: mergedProperties,
        schemaMap: debugMeta?.schemaMap,
        specValidated: debugMeta?.specValidated,
        userId: this.userId || undefined,
      });

      const analytics = await this.initialize();
      if (!analytics) return;

      analytics.track(event, mergedProperties);
    } catch (error) {
      console.warn('Segment track failed (ignored)', error);
    }
  }

  /**
   * Track an arbitrary event name (useful for 3rd-party integrations like
   * Amplitude Experiment exposure events).
   */
  async trackRaw(event: string, properties?: Record<string, any>): Promise<void> {
    try {
      const analytics = await this.initialize();
      if (!analytics) return;
      analytics.track(event, properties);
    } catch (error) {
      console.warn('Segment trackRaw failed (ignored)', error);
    }
  }

  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    try {
      const analytics = await this.initialize();
      if (!analytics) return;

      analytics.page(name, properties);
    } catch (error) {
      console.warn('Segment page failed (ignored)', error);
    }
  }

  // Utility methods to set context
  setRoomContext({
    roomName,
    gameInstanceId,
  }: {
    roomName?: string;
    gameInstanceId?: string;
  }): void {
    if (roomName) {
      this.roomName = roomName;
    }
    if (gameInstanceId) {
      this.gameInstanceId = gameInstanceId;
    }
  }

  clearRoomContext(): void {
    this.roomName = null;
    this.gameInstanceId = null;
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  getUserId(): string | null {
    return this.userId;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  async getAnonymousId(): Promise<string | undefined> {
    try {
      const analytics = await this.initialize();
      if (!analytics) return undefined;

      const id = analytics.user().anonymousId();
      return id || undefined;
    } catch (error) {
      console.warn('Segment getAnonymousId failed (ignored)', error);
      return undefined;
    }
  }

  // Reset analytics (useful for logout)
  async reset(): Promise<void> {
    try {
      const analytics = await this.initialize();
      if (!analytics) return;

      analytics.reset();
      this.userId = null;
      this.sessionId = this.generateSessionId();
      this.clearRoomContext();
    } catch (error) {
      console.warn('Segment reset failed (ignored)', error);
    }
  }

  // Session Replay utility methods
  getSessionReplayProperties(): Record<string, any> | null {
    if (!this.sessionReplayInitialized) {
      console.warn('Session Replay not initialized');
      return null;
    }
    return sessionReplay.getSessionReplayProperties();
  }

  isSessionReplayInitialized(): boolean {
    return this.sessionReplayInitialized;
  }

  // Manual session ID update (for debugging or manual control)
  updateSessionReplayId(sessionId: number): void {
    if (!this.sessionReplayInitialized) {
      console.warn('Session Replay not initialized');
      return;
    }
    setCookie('analytics_session_id', sessionId.toString());
    sessionReplay.setSessionId(sessionId);
    console.log(`Session Replay ID updated to: ${sessionId}`);
  }
}

// Export singleton instance
export const segmentClient = new SegmentClient();

// Export types for use in components
export type { TrackEvent, GameEventProperties };
